import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setDraftCount } from '~/actions/PermanentJobs/PermanentJobs';
import { ConfirmPopup, Text } from '~/commonComponents';
import AnimatedCheckCircle from '~/components/shared/AnimatedCheckCircle';
import { useDeleteDraftJob } from '~/domain/PermanentJobs';
import { useAppSelector } from '~/hooks';
import { IToggleControls } from '~/hooks/useToggle';
import WarningIcon from '~/images/icons/Warning';
import { Colors } from '~/themes/colors';

interface IProps {
  controls: IToggleControls;
  jobPostingId: string;
}

const SuccessIconComponent = () => <AnimatedCheckCircle />;
const WarningIconComponent = () => <WarningIcon width={80} height={80} />;

export const DeleteDraftPopupConfirmation = ({ controls, jobPostingId }: IProps) => {
  const dispatch = useDispatch();
  const draftCount = useAppSelector((state) => state.permanentJobs.draftCount);
  const { isError, isPending, isSuccess, mutate, reset } = useDeleteDraftJob();

  const handleConfirmDelete = useCallback(
    () =>
      mutate(
        { jobPostingId },
        {
          onSuccess: () => {
            dispatch(
              setDraftCount(Math.max(0, (draftCount ?? 1) - 1)),
            );
          },
        },
      ),
    [dispatch, draftCount, jobPostingId, mutate],
  );

  const handleClosePopup = useCallback(() => {
    reset();
    controls.setOff();
  }, [controls, reset]);

  useEffect(() => {
    if (isError) {
      handleClosePopup();
    }
  }, [handleClosePopup, isError]);

  return (
    <>
      {!isSuccess ? (
        // @ts-expect-error - ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={WarningIconComponent}
          title="Delete Draft"
          titleStyle={{ marginTop: 21, marginBottom: 16 }}
          description={
            <Text color={Colors.neutral_500}>
              Are you sure you want to delete this draft? This process
              <br />
              cannot be undone.
            </Text>
          }
          descriptionStyle={{ width: '100%', marginBottom: 38 }}
          modalStyle={{ width: 560 }}
          leftButtonAction={handleClosePopup}
          leftButtonDataCy="delete-draft-go-back-button"
          leftButtonDisabled={isPending}
          leftButtonText="Go Back"
          rightButtonAction={handleConfirmDelete}
          rightButtonDataCy="delete-draft-confirm-button"
          rightButtonLoading={isPending}
          rightButtonText="Delete"
        />
      ) : (
        // @ts-expect-error - ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={SuccessIconComponent}
          title="Draft Deleted!"
          titleStyle={{ marginTop: 21, marginBottom: 16 }}
          description={
            <Text color={Colors.neutral_500}>Your draft has been deleted successfully.</Text>
          }
          descriptionStyle={{ width: '100%', marginBottom: 38 }}
          modalStyle={{ width: 560 }}
          rightButtonDataCy="delete-draft-close-button"
          rightButtonText="Close"
          rightButtonAction={handleClosePopup}
        />
      )}
    </>
  );
};
